/**
 * /verify-email — landing page for the verification link sent to a new
 * account's inbox. Phase B production-hardening (2026-02).
 *
 * Behaviour:
 *  - If URL has ?token=... → POST /api/auth/verify-email immediately and
 *    show success / expired / invalid based on the structured detail
 *    reason returned by the backend.
 *  - On success the backend returns a fresh JWT + user — we hand both to
 *    AuthContext.adoptSession() so the user is signed in straight away
 *    and lands on their role dashboard with no extra step.
 *  - If URL has ?pending=true&email=... (post-registration redirect) we
 *    render a "check your inbox" message + a resend button (rate-limited
 *    server-side, so the button is safe to expose).
 *
 * Zero dependencies on dispatch / state machine / janitor / offer routing.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, tokenStore, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

const dashboardFor = (role) =>
  role === "admin" ? "/admin" : role === "driver" ? "/driver" : "/passenger";

// Module-level cache of in-flight verification requests, keyed by token.
// The verification token is SINGLE-USE (the backend clears it on success), so
// it must be POSTed exactly ONCE. A per-instance useRef guard is not enough:
// on page load the auth-loading gate swaps the component tree, REMOUNTING this
// page with a fresh ref — which previously fired a second request that hit a
// 400 ("already used") and rendered a spurious "link invalid" on a SUCCESSFUL
// verification. A module-level promise survives remounts and StrictMode
// double-invokes: the request runs once and every mounted instance renders the
// same result, so the spinner always resolves. (Production incident 2026-06-09.)
const verifyRequests = new Map();

export default function VerifyEmail() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const pendingEmail = search.get("email") || "";
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  // Three explicit states so the UI can render a friendly message for
  // each branch without nesting ternaries:
  //   loading      — POST in flight
  //   ok           — token verified, user logged in (auto-redirect)
  //   invalid      — token wrong / already-used (no resend offered;
  //                  the link is genuinely useless)
  //   expired      — token expired (resend offered)
  //   pending      — no token in URL — we're on the post-register screen
  //   pending_sent — resend just succeeded
  const [phase, setPhase] = useState(token ? "loading" : "pending");
  const [resendEmail, setResendEmail] = useState(pendingEmail);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    // Share ONE network request per token across all mounts/instances.
    if (!verifyRequests.has(token)) {
      verifyRequests.set(token, api.post("/auth/verify-email", { token }));
    }
    verifyRequests.get(token).then(
      async (res) => {
        if (!active) return;
        const { token: jwt, user } = res.data;
        tokenStore.set(jwt);
        if (adoptSession) await adoptSession(jwt, user);
        toast.success("Email verified — welcome to Crystal Dispatch.");
        setPhase("ok");
        setTimeout(() => navigate(dashboardFor(user.role), { replace: true }), 1200);
      },
      (e) => {
        if (!active) return;
        const reason = e?.response?.data?.detail?.reason;
        if (reason === "expired_token") setPhase("expired");
        else setPhase("invalid");
      }
    );
    return () => { active = false; };
    // Depend ONLY on `token`: navigate/adoptSession are recreated on
    // AuthProvider re-renders and must not retrigger verification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = async (e) => {
    e?.preventDefault();
    if (!resendEmail) {
      toast.error("Enter your email address first.");
      return;
    }
    setResending(true);
    try {
      await api.post("/auth/verify-resend", { email: resendEmail });
      toast.success("If your account needs verification, a new link is on the way.");
      setPhase("pending_sent");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not resend — please try again later.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="border border-border bg-card p-8" data-testid="verify-email-card">
          {phase === "loading" && (
            <div className="text-center" data-testid="verify-email-loading">
              <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground animate-spin" />
              <h1 className="font-heading text-2xl font-black mt-4">Verifying your email…</h1>
              <p className="text-sm text-muted-foreground mt-2">One moment, please.</p>
            </div>
          )}

          {phase === "ok" && (
            <div className="text-center" data-testid="verify-email-ok">
              <div className="w-14 h-14 rounded-full bg-[#10B981] mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">You&apos;re in.</h1>
              <p className="text-sm text-muted-foreground mt-2">Redirecting to your dashboard…</p>
            </div>
          )}

          {phase === "invalid" && (
            <div className="text-center" data-testid="verify-email-invalid">
              <div className="w-14 h-14 rounded-full bg-[#EF4444] mx-auto flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">Link no longer valid</h1>
              <p className="text-sm text-muted-foreground mt-2">
                This verification link is invalid or has already been used. If your account
                is still unverified, request a fresh link below.
              </p>
              <form onSubmit={handleResend} className="mt-6 text-left space-y-3">
                <Label htmlFor="resend-email" className="label-mini">Email</Label>
                <Input
                  id="resend-email" type="email" required
                  value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="verify-email-resend-input"
                />
                <Button type="submit" disabled={resending} className="w-full" data-testid="verify-email-resend-btn">
                  {resending ? "Sending…" : "Send a new verification email"}
                </Button>
              </form>
            </div>
          )}

          {phase === "expired" && (
            <div className="text-center" data-testid="verify-email-expired">
              <div className="w-14 h-14 rounded-full bg-[#F59E0B] mx-auto flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">Link expired</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Verification links expire after 24 hours. Request a fresh one below.
              </p>
              <form onSubmit={handleResend} className="mt-6 text-left space-y-3">
                <Label htmlFor="resend-email" className="label-mini">Email</Label>
                <Input
                  id="resend-email" type="email" required
                  value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="verify-email-resend-input-expired"
                />
                <Button type="submit" disabled={resending} className="w-full" data-testid="verify-email-resend-btn-expired">
                  {resending ? "Sending…" : "Send a fresh verification email"}
                </Button>
              </form>
            </div>
          )}

          {(phase === "pending" || phase === "pending_sent") && (
            <div className="text-center" data-testid="verify-email-pending">
              <div className="w-14 h-14 rounded-full bg-[#FFC107] mx-auto flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#0A0A0A]" />
              </div>
              <h1 className="font-heading text-2xl font-black mt-4">
                {phase === "pending_sent" ? "Check your inbox" : "Verify your email"}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {pendingEmail ? (
                  <>We&apos;ve sent a verification link to <strong>{pendingEmail}</strong>. Click it to activate your account.</>
                ) : (
                  <>Check your inbox for the activation link we just sent. Click it to finish signing up.</>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                Didn&apos;t get it? It can take a minute or two. Check spam too.
              </p>
              <form onSubmit={handleResend} className="mt-6 text-left space-y-3">
                <Label htmlFor="resend-email" className="label-mini">Email</Label>
                <Input
                  id="resend-email" type="email" required
                  value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="verify-email-resend-input-pending"
                />
                <Button type="submit" disabled={resending} variant="outline" className="w-full" data-testid="verify-email-resend-btn-pending">
                  {resending ? "Sending…" : "Resend verification email"}
                </Button>
              </form>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground" data-testid="verify-email-back-to-login">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
